import {useEffect, useRef, useState} from "react";

import {useLevelOrder} from "@hooks/LevelOrder";
import {clearView, viewProduct} from "@redux/slices/productSlices/viewSlice";
import {BackButton} from "@ui/buttons/BackButton";
import {LevelDocuments} from "@ui/forms/LevelDocuments";
import {ViewField} from "@ui/forms/ViewField";
import {ViewTitle} from "@ui/forms/ViewTitle";
import {BorderArticle} from "@ui/general/BorderArticle";
import {PreviewCard} from "@ui/general/PreviewCard";
import {Spinner} from "@ui/general/Spinner";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";

import styles from "./styles.module.scss";

export const ProductViewPage = () => {
  const {productLevels} = useLevelOrder();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {id} = useParams();

  const {isMobile} = useSelector((state) => state.common.settings);
  const {current, status} = useSelector((state) => state.product.view);

  const [groupedFiles, setGroupedFiles] = useState([]);

  const productRef = useRef();

  const handleClickEdit = (id) => {
    navigate(`/admin-panel/products/edit/${id}`, {state: {id}});
  };

  useEffect(() => {
    dispatch(viewProduct({id}));

    return () => {
      dispatch(clearView());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (current?.product_version?.files) {
      const filesByLevel =
        productLevels &&
        productLevels.map((level) => ({
          level: level.value,
          file: current.product_version.files.filter(
            (file) => file.level === level.value,
          ),
        }));

      setGroupedFiles(filesByLevel);
    }
  }, [current, productLevels]);

  useEffect(() => {
    if (productRef.current && isMobile) {
      productRef.current.scrollIntoView({behavior: "smooth"});
    }
  }, [isMobile]);

  return (
    <div
      ref={productRef}
      className={styles.productWrapper}
    >
      <BorderArticle>
        <div className={styles.product}>
          <BackButton />

          {status === "loading" ? (
            <Spinner
              width={80}
              height={80}
            />
          ) : (
            <div className={styles.productContent}>
              <div className={styles.productContentHeadWrapper}>
                <div className={styles.productContentHead}>
                  <ViewTitle
                    value={current?.product?.name}
                    handleClickEdit={() =>
                      handleClickEdit(current?.product?.id)
                    }
                  />

                  <ViewField
                    label={t("input.preview")}
                    direction={"col"}
                    customContent={
                      <PreviewCard
                        name={current?.product?.name}
                        mask={current?.product?.mask}
                        image={current?.product?.cover}
                      />
                    }
                  />
                </div>
              </div>

              <ul className={styles.productContentBody}>
                <ViewField
                  label={t("input.country")}
                  value={current?.product?.country}
                />

                <ViewField
                  label={t("input.product_line")}
                  value={current?.product?.product_line}
                />

                <ViewField
                  label={t("input.related_documents_view")}
                  customContent={
                    <div className={styles.productLevelList}>
                      {groupedFiles && groupedFiles.length > 0
                        ? groupedFiles.map((item) => (
                            <LevelDocuments
                              key={item?.level}
                              level={item?.level}
                              files={item?.file}
                            />
                          ))
                        : "-"}
                    </div>
                  }
                />

                <ViewField
                  label={t("input.questions")}
                  value={current?.product_version?.questions}
                />
              </ul>
            </div>
          )}
        </div>
      </BorderArticle>
    </div>
  );
};
